import React, { useEffect, useState } from 'react';
import { Coins, KeyRound, Sparkles, Gem, Crown, ShoppingBag, X } from 'lucide-react';
import { SKINS, TRAILS, AURAS, COSMETICS, PETS, GADGETS } from '../game/config';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const COIN_ICON = {
  coins_1000: Coins, coins_3000: Coins, coins_7000: Gem, coins_16000: Gem, coins_35000: Crown, coins_75000: Crown, coins_120000: Crown,
};

// look up a display icon/name for a granted item
const ITEM_LOOKUP = { skins: SKINS, trails: TRAILS, auras: AURAS, pets: PETS, gadgets: GADGETS,
  hats: COSMETICS.hats.reduce((o, i) => (o[i.id] = i, o), {}),
  effects: COSMETICS.effects.reduce((o, i) => (o[i.id] = i, o), {}) };
const grantIcon = (g) => ITEM_LOOKUP[g.cat]?.[g.id]?.icon || '🎁';
const grantName = (g) => ITEM_LOOKUP[g.cat]?.[g.id]?.name || g.id;

export default function CoinStore({ onClose }) {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(API + '/payments/packs').then((r) => r.json()).then((d) => setPacks(d.packs || [])).catch(() => {});
  }, []);

  const buy = async (lookup_key) => {
    setLoading(lookup_key); setError('');
    try {
      const r = await fetch(API + '/payments/checkout', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lookup_key, origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (d.checkout_url) {
        try {
          const pend = JSON.parse(localStorage.getItem('so_pending') || '[]');
          if (d.session_id && !pend.includes(d.session_id)) pend.push(d.session_id);
          localStorage.setItem('so_pending', JSON.stringify(pend));
        } catch {}
        window.location.href = d.checkout_url;
      } else { setError('Could not start checkout. Please try again.'); setLoading(null); }
    } catch (e) { setError('Network error — please try again.'); setLoading(null); }
  };

  const coinPacks = packs.filter((p) => p.kind === 'coins');
  const keyPacks = packs.filter((p) => p.kind === 'keys');
  const bundles = packs.filter((p) => p.kind === 'bundle');
  const price = (p) => (loading === p.lookup_key ? '…' : (p.amount != null ? '$' + (p.amount / 100).toFixed(2) : '—'));

  return (
    <div className="so-overlay" onClick={onClose} data-testid="coin-store">
      <div className="so-panel so-store-panel" onClick={(e) => e.stopPropagation()}>
        <button className="so-panel-x" onClick={onClose} data-testid="coin-store-close"><X size={18} /></button>
        <h2><ShoppingBag size={22} style={{ verticalAlign: '-4px' }} /> Premium Store</h2>
        <p className="so-store-sub">Real-money coins, keycards & exclusive bundles. Test card: 4242 4242 4242 4242.</p>
        {error && <p className="so-store-err" data-testid="coin-store-error">{error}</p>}

        {/* Premium bundles */}
        <div className="so-store-section-title"><Crown size={15} /> Premium Bundles</div>
        <div className="so-bundle-grid">
          {bundles.map((p) => (
            <button key={p.lookup_key} className="so-bundle" onClick={() => buy(p.lookup_key)} disabled={loading} data-testid={'buy-' + p.lookup_key}>
              {p.tag && <span className="so-store-tag gold">{p.tag}</span>}
              <b className="so-bundle-name">{p.name}</b>
              <div className="so-bundle-grants">
                {p.coins > 0 && <span className="so-grant"><Coins size={13} /> {p.coins.toLocaleString()}</span>}
                {p.keys > 0 && <span className="so-grant"><KeyRound size={13} /> {p.keys}</span>}
                {(p.items || []).map((g, i) => (
                  <span key={i} className="so-grant exclusive" title={grantName(g)}>{grantIcon(g)} {grantName(g)}</span>
                ))}
              </div>
              <span className="so-store-price big">{price(p)}</span>
            </button>
          ))}
        </div>

        {/* Coin packs */}
        <div className="so-store-section-title"><Coins size={15} /> Coin Packs</div>
        <div className="so-store-grid">
          {coinPacks.map((p) => {
            const Ic = COIN_ICON[p.lookup_key] || Coins;
            return (
              <button key={p.lookup_key} className={'so-store-item' + (p.tag === 'Best Value' ? ' best' : '')}
                onClick={() => buy(p.lookup_key)} disabled={loading} data-testid={'buy-' + p.lookup_key}>
                {p.tag && <span className="so-store-tag">{p.tag}</span>}
                <span className="so-store-emoji"><Ic size={26} /></span>
                <b>{p.coins.toLocaleString()}</b>
                <small>coins{p.keys > 0 ? ` +${p.keys}🔑` : ''}</small>
                <span className="so-store-price">{price(p)}</span>
              </button>
            );
          })}
        </div>

        {/* Keycard packs */}
        <div className="so-store-section-title"><KeyRound size={15} /> Keycard Packs</div>
        <div className="so-store-grid">
          {keyPacks.map((p) => (
            <button key={p.lookup_key} className={'so-store-item key' + (p.tag === 'Best Value' ? ' best' : '')}
              onClick={() => buy(p.lookup_key)} disabled={loading} data-testid={'buy-' + p.lookup_key}>
              {p.tag && <span className="so-store-tag">{p.tag}</span>}
              <span className="so-store-emoji"><KeyRound size={24} /></span>
              <b>{p.keys}</b>
              <small>keycards</small>
              <span className="so-store-price">{price(p)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
